/**
 * TextChatSDK - Persistent WebSocket connection to Flux backend
 * - Opens a single WS to /chat/text on connect, keeps it alive for the session
 * - Same default WebSocket base as Voice SDK v2; optional `websocketUrl` overrides (e.g. staging).
 * - Sends text messages on the same connection (no open/close per message)
 * - Streams chunks back, emits events (chunk, done, error)
 * - Auto-reconnects on disconnect with conversationId for session continuity
 * - Persists conversationId to localStorage
 */
import EventEmitter from './EventEmitter.js';
import ClientToolsRegistry from './ClientToolsRegistry.js';
import { ClientScriptManager } from './ClientScriptManager.js';

const RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_ATTEMPTS = 5;
const TEXT_CHAT_PATH = '/chat/text';
/** Production voice/text base (path swapped to /chat/text); matches VoiceSDK v2 default. */
const DEFAULT_WEBSOCKET_BASE_URL = 'wss://speech.talktopc.com/ws/conv';

export default class TextChatSDK extends EventEmitter {
  constructor(config = {}) {
    super();
    
    const scriptConfig = this.readScriptTagConfig();

    this.config = {
      websocketUrl: config.websocketUrl,
      appId: config.appId || scriptConfig.appId,
      agentId: config.agentId || scriptConfig.agentId,
      conversationId: config.conversationId !== undefined ? config.conversationId : this.getPersistedConversationId(),
      forceConversationId: config.forceConversationId,
      ...config
    };
    
    this.ws = null;
    this.connected = false;
    this.connecting = false;
    this.reconnectAttempts = 0;
    this.intentionalClose = false;
    this.fullResponseBuffer = '';
    this.queue = [];
    this.inFlight = false;

    // Client-tool registry. Reads from a shared handler Map (owned by TTPChatWidget
    // so voice + text resolve handlers from the same place). Writes responses
    // back through THIS WebSocket — VoiceSDK has its own registry that writes
    // through the voice WebSocket, so each channel responds where the call came in.
    this.clientToolsRegistry = new ClientToolsRegistry(
      (message) => this.sendRaw(message),
      () => this.config.conversationId,
      config.sharedToolHandlers || null
    );

    // Flavor-independent client-tool script manager (reserved partner id
    // `__client_tools__`). Owns its own PartnerScriptRunner so client-tool
    // scripts run regardless of flavor, alongside (and separate from) the
    // ecommerce flavor's partner runner. Sends result/emit frames over this
    // text-chat WebSocket so the response returns on the channel it arrived on.
    this.clientScriptManager = new ClientScriptManager({
      send: (message) => this.sendRaw(message),
      label: 'text',
    });
  }

  /**
   * Send a raw JSON message immediately on the open WebSocket. Used by the
   * client-tool registry — bypasses the LLM-turn queue used by sendMessage().
   */
  sendRaw(message) {
    if (!this.connected || !this.ws) {
      console.warn('[TextChatSDK] Cannot send — WebSocket not connected');
      return;
    }
    try {
      this.ws.send(typeof message === 'string' ? message : JSON.stringify(message));
    } catch (e) {
      console.error('[TextChatSDK] sendRaw failed:', e);
    }
  }

  readScriptTagConfig() {
    try {
      const scripts = Array.from(document.getElementsByTagName('script'));
      const sdkScript = scripts.reverse().find(s => (s.dataset && (s.dataset.appId || s.dataset.agentId)) || (s.src && s.src.includes('agent-widget')));
      if (!sdkScript) return {};
      return {
        appId: sdkScript.dataset.appId,
        agentId: sdkScript.dataset.agentId
      };
    } catch (_) {
      return {};
    }
  }

  /**
   * Derive wss://…/chat/text from the voice-style base (default production host if omitted).
   */
  buildWebSocketUrl() {
    const baseUrl = this.config.websocketUrl || DEFAULT_WEBSOCKET_BASE_URL;

    try {
      const url = new URL(baseUrl);
      url.pathname = TEXT_CHAT_PATH;
      // Clear any existing search params from the base URL
      url.search = '';
      
      if (this.config.appId) url.searchParams.append('appId', this.config.appId);
      if (this.config.agentId) url.searchParams.append('agentId', this.config.agentId);
      return url.toString();
    } catch (_) {
      // Fallback: manual string manipulation if URL parsing fails
      const origin = baseUrl.replace(/\/[^/]*$/, '').replace(/\?.*$/, '');
      const params = new URLSearchParams();
      if (this.config.appId) params.append('appId', this.config.appId);
      if (this.config.agentId) params.append('agentId', this.config.agentId);
      return `${origin}${TEXT_CHAT_PATH}?${params.toString()}`;
    }
  }

  /**
   * Connect to the Flux text chat WebSocket.
   * Called automatically on first sendMessage, or can be called explicitly.
   */
  connect() {
    if (this.connected || this.connecting) return;
    if (!this.config.appId || !this.config.agentId) {
      console.error('[TextChatSDK] Cannot connect - missing appId or agentId');
      return;
    }
    this.connecting = true;
    this.intentionalClose = false;
    const url = this.buildWebSocketUrl();
    console.log('[TextChatSDK] Connecting to:', url);

    try {
      this.ws = new WebSocket(url);
    } catch (e) {
      this.connecting = false;
      this.emit('error', e);
      return;
    }

    this.ws.onopen = () => {
      this.connecting = false;
      this.connected = true;
      this.reconnectAttempts = 0;
      console.log('[TextChatSDK] Connected');

      // Send hello immediately on connect (same fields as voice SDK v2 hello).
      this.sendHelloMessage();
    };

    this.ws.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data);

        // Server-initiated client tool call — dispatch to the shared registry,
        // which executes the registered handler and sends client_tool_result
        // back through this WebSocket.
        if (data.t === 'client_tool_call') {
          // Partner-script dispatch for the sentinel __client_tools__ partner
          // id rides on client_tool_call (toolName 'run_partner_script') —
          // route it to the flavor-independent ClientScriptManager instead of
          // the registry (which would NO_HANDLER it when no flavor is loaded).
          if (ClientScriptManager.isClientToolsToolCall(data)) {
            this.clientScriptManager.handleClientToolCall(data, this.config.conversationId);
            this.emit('message', data);
            return;
          }
          this.clientToolsRegistry.handleToolCall(data);
          this.emit('message', data);
          return;
        }

        // Async partner-adapter dispatch (separate from client_tool_call — no
        // toolCallId, no LLM tool_call closure). Backend already returned a
        // `clientSide<X>:true` placeholder synchronously and registered a
        // callback keyed by requestId. We reuse the 'run_partner_script' tool
        // handler (registered by the ecommerce flavor via buildRunPartnerScriptTool),
        // then post the outcome back as a separate run_partner_script_result
        // envelope which TextChatWsHandler routes to the callback.
        if (data.t === 'run_partner_script') {
          this._handleRunPartnerScript(data);
          this.emit('message', data);
          return;
        }

        // Flavor-independent client-tool bundle (partner_id `__client_tools__`)
        // is loaded by the dedicated ClientScriptManager. Every other partner id
        // falls through to the flavor (via the emitted 'message' event), so the
        // ecommerce flavor's own bundle handling keeps working untouched.
        if (data.t === 'partner_bundle' && ClientScriptManager.isClientToolsBundle(data)) {
          this.clientScriptManager.setBundle(data);
          this.emit('message', data);
          return;
        }

        // Server responds with hello_ack (same format as voice SDK)
        if (data.t === 'hello_ack') {
          if (!data.ok) {
            console.error('[TextChatSDK] hello_ack error:', data.error);
            this.emit('error', new Error(data.error || 'Server rejected hello'));
            return;
          }
          console.log('[TextChatSDK] Session ready, conversationId:', data.conversationId);
          if (data.conversationId) {
            this.config.conversationId = data.conversationId;
            this.persistConversationId(data.conversationId);
            this.emit('conversationIdChanged', data.conversationId);
          }
          // Emit disclaimers if required (widget handles the UI)
          if (data.disclaimersRequired) {
            this.emit('disclaimersRequired', {
              texts: data.disclaimerTexts,
              hash: data.disclaimersHash
            });
          }
          // Tools are loaded, connection is ready — drain any queued messages
          this.drainQueue();
          return;
        }

        if (data.type === 'chunk' && typeof data.content === 'string') {
          this.fullResponseBuffer += data.content;
          this.emit('chunk', data.content);
        } else if (data.type === 'done') {
          const finalText = data.text || data.content || '';
          if (finalText && !this.fullResponseBuffer) {
            this.fullResponseBuffer = finalText;
          }
          this.emit('done', { text: this.fullResponseBuffer });
          // Resolve the pending promise
          if (this._currentTask) {
            this._currentTask.resolve({
              conversationId: this.config.conversationId,
              fullText: this.fullResponseBuffer
            });
            this._currentTask = null;
          }
          this.inFlight = false;
          this.drainQueue();
        } else if (data.type === 'error') {
          const err = new Error(data.message || 'Server error');
          this.emit('error', err);
          if (this._currentTask) {
            this._currentTask.reject(err);
            this._currentTask = null;
          }
          this.inFlight = false;
          this.drainQueue();
        }

        // Emit raw 'message' event for all messages (including show_media, show_items, etc.)
        // Flavor modules listen on this to handle tool widget messages.
        this.emit('message', data);
      } catch (e) {
        // Non-JSON or parse error; ignore
      }
    };

    this.ws.onerror = (e) => {
      console.error('[TextChatSDK] WebSocket error:', e);
      this.emit('error', e);
    };

    this.ws.onclose = (event) => {
      this.connected = false;
      this.connecting = false;
      console.log('[TextChatSDK] Disconnected, code:', event.code, 'reason:', event.reason);

      // Domain whitelist error — don't reconnect
      if (event.code === 1008 && event.reason &&
          (event.reason.includes('Domain not whitelisted') ||
           event.reason.includes('domain') ||
           event.reason.includes('whitelist'))) {
        const domainError = new Error('DOMAIN_NOT_WHITELISTED');
        domainError.reason = event.reason;
        domainError.code = event.code;
        this.emit('domainError', domainError);
        this.rejectPendingTasks(domainError);
        return;
      }

      // Auth failure — don't reconnect
      if (event.code === 4001 || event.code === 4003) {
        const authError = new Error(event.reason || 'Authentication failed');
        this.emit('error', authError);
        this.rejectPendingTasks(authError);
        return;
      }

      // If not intentional, try to reconnect
      if (!this.intentionalClose && this.reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
        this.reconnectAttempts++;
        const delay = RECONNECT_DELAY_MS * this.reconnectAttempts;
        console.log(`[TextChatSDK] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`);
        setTimeout(() => this.connect(), delay);
      } else if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        const err = new Error('Max reconnection attempts reached');
        this.emit('error', err);
        this.rejectPendingTasks(err);
      }
    };
  }

  /**
   * Send a text message. Returns a Promise that resolves with the full response.
   * Connects automatically if not already connected.
   */
  async sendMessage(text) {
    return new Promise((resolve, reject) => {
      if (!text || !text.trim()) {
        reject(new Error('Message is empty'));
        return;
      }
      if (!this.config.appId || !this.config.agentId) {
        reject(new Error('Missing appId or agentId'));
        return;
      }

      const task = { text, resolve, reject };

      // Connect if not connected
      if (!this.connected && !this.connecting) {
        this.queue.push(task);
        this.connect();
        return;
      }

      // Queue if in-flight or still connecting
      if (this.inFlight || this.connecting || !this.connected) {
        this.queue.push(task);
        return;
      }

      this.executeTask(task);
    });
  }

  executeTask(task) {
    this.inFlight = true;
    this.fullResponseBuffer = '';
    this._currentTask = task;

    try {
      const payload = JSON.stringify({ type: 'text', message: task.text });
      console.log('[TextChatSDK] Sending:', payload);
      this.ws.send(payload);
    } catch (e) {
      this.inFlight = false;
      this._currentTask = null;
      task.reject(e);
      this.emit('error', e);
    }
  }

  drainQueue() {
    if (this.queue.length === 0 || this.inFlight || !this.connected) return;
    const next = this.queue.shift();
    this.executeTask(next);
  }

  rejectPendingTasks(error) {
    if (this._currentTask) {
      this._currentTask.reject(error);
      this._currentTask = null;
    }
    this.inFlight = false;
    while (this.queue.length > 0) {
      const task = this.queue.shift();
      task.reject(error);
    }
  }

  /**
   * Handle a run_partner_script envelope (async partner-adapter dispatch).
   * Mirrors VoiceSDK_v2._handleRunPartnerScript so text and voice both close
   * the loop the same way:
   *   backend → widget : { t:'run_partner_script', requestId, partnerId, action, args }
   *   widget  → backend: { t:'run_partner_script_result', requestId, partnerId, action, ok, result|error }
   * @private
   */
  async _handleRunPartnerScript(message) {
    const { requestId, partnerId, action } = message || {};
    const args = (message && message.args) || {};

    if (!requestId) {
      console.warn('[TextChatSDK] run_partner_script missing requestId; dropping', message);
      return;
    }

    // Route by partner id. The reserved `__client_tools__` partner is owned by
    // the flavor-independent ClientScriptManager (its own runner + 50KB cap +
    // result envelope). Every other partner id resolves the single
    // 'run_partner_script' tool handler registered by the ecommerce flavor.
    if (ClientScriptManager.isClientToolsScript(message)) {
      return this.clientScriptManager.handleRunPartnerScript(message);
    }

    const handler = this.clientToolsRegistry?.handlers?.get('run_partner_script');
    if (!handler) {
      console.error(`[TextChatSDK] no run_partner_script handler registered (ecommerce flavor not loaded?); requestId=${requestId} partner=${partnerId} action=${action}`);
      this.sendRaw({
        t: 'run_partner_script_result',
        requestId,
        partnerId,
        action,
        ok: false,
        error: 'no_handler_registered',
      });
      return;
    }

    const startedAt = Date.now();
    try {
      const result = await handler({ action, args });
      const ok = result && typeof result === 'object' && 'ok' in result ? !!result.ok : true;
      const elapsedMs = Date.now() - startedAt;
      if (ok) {
        console.log(`[TextChatSDK] run_partner_script ok partner=${partnerId} action=${action} requestId=${requestId} (${elapsedMs}ms)`);
      } else {
        const reason = (result && (result.reason || result.error)) || 'unknown';
        console.warn(`[TextChatSDK] run_partner_script failed partner=${partnerId} action=${action} requestId=${requestId} reason=${reason} (${elapsedMs}ms)`, result);
      }
      this.sendRaw({
        t: 'run_partner_script_result',
        requestId,
        partnerId,
        action,
        ok,
        result,
      });
    } catch (err) {
      const elapsedMs = Date.now() - startedAt;
      console.error(`[TextChatSDK] run_partner_script threw partner=${partnerId} action=${action} requestId=${requestId} (${elapsedMs}ms):`, err);
      this.sendRaw({
        t: 'run_partner_script_result',
        requestId,
        partnerId,
        action,
        ok: false,
        error: (err && err.message) || String(err),
      });
    }
  }

  /**
   * Send hello message with all supported fields (matching voice SDK v2 structure).
   */
  sendHelloMessage() {
    if (!this.connected || !this.ws) return;

    const helloMessage = {
      t: 'hello',
      v: 2
    };

    // Flavor (partner tools: hotels, restaurants, ecommerce, etc.)
    if (this.config.flavor) {
      helloMessage.flavor = this.config.flavor;
    }

    // Variables (template variables injected into agent prompt)
    if (this.config.variables && Object.keys(this.config.variables).length > 0) {
      helloMessage.variables = this.config.variables;
    }

    // Agent settings override (prompt, LLM model, temperature, etc.)
    if (this.config.agentSettingsOverride) {
      helloMessage.agentSettingsOverride = this.config.agentSettingsOverride;
    }

    // LLM provider override (per-session)
    if (this.config.llmProvider) {
      helloMessage.llmProvider = this.config.llmProvider;
    }

    // Auto-detect browser locale
    helloMessage.locale = this.detectBrowserLocale();

    // Page context is intentionally NOT attached to the hello message.
    // The backend caches the system prompt across turns and pulls page
    // context on demand via the `get_page_context` control message
    // (handled by the voice SDK; text-chat sessions don't currently use
    // visual tools).

    // SDK build time for debugging
    if (typeof __BUILD_TIME__ !== 'undefined') {
      helloMessage.lastBuildTime = __BUILD_TIME__;
    }

    try {
      this.ws.send(JSON.stringify(helloMessage));
      console.log('[TextChatSDK] Sent hello:', Object.keys(helloMessage));
    } catch (_) {}
  }

  /**
   * Detect browser language from navigator (same as VoiceSDK v2).
   */
  detectBrowserLocale() {
    try {
      const fullLocale = navigator.language || navigator.languages?.[0] || 'en-US';
      const language = fullLocale.split('-')[0]?.toLowerCase() || 'en';
      return { language, fullLocale };
    } catch (_) {
      return { language: 'en', fullLocale: 'en-US' };
    }
  }

  persistConversationId(conversationId) {
    try {
      if (conversationId) localStorage.setItem('ttp_text_chat_conversation_id', conversationId);
    } catch (_) {}
  }

  getPersistedConversationId() {
    try {
      return localStorage.getItem('ttp_text_chat_conversation_id') || null;
    } catch (_) {
      return null;
    }
  }

  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Disconnect and clean up.
   */
  destroy() {
    this.intentionalClose = true;
    this.queue = [];
    this.inFlight = false;
    this._currentTask = null;
    if (this.ws) {
      try { this.ws.close(); } catch (_) {}
      this.ws = null;
    }
    this.connected = false;
    this.connecting = false;
    this.removeAllListeners();
  }
}
